import { useEffect, useState } from "react";
import { ImageSquare } from "@phosphor-icons/react";
import { getDescriptorFonts, loadDescriptorFont, descriptorStyles } from "@/lib/descriptorFonts";

const POS = {
  top_left: "top-0 left-0 items-start justify-start",
  top_right: "top-0 right-0 items-start justify-end",
  bottom_left: "bottom-0 left-0 items-end justify-start",
  bottom_right: "bottom-0 right-0 items-end justify-end",
};
const SIZE = { small: "h-6", medium: "h-10", large: "h-16" };
// Descriptor sample sits in the opposite corner to the logo so both are visible in the mock.
const OPP = { top_left: "bottom_right", top_right: "bottom_left", bottom_left: "top_right", bottom_right: "top_left" };

export function BrandPreview({ brand = {}, className = "" }) {
  const pos = POS[brand.logo_position] || POS.top_right;
  const size = SIZE[brand.logo_size] || SIZE.medium;
  const pad = brand.safe_area ? "p-[8%]" : "p-[2%]";
  const accent = brand.accent_color || "#3A86FF";

  // Resolve the selected descriptor font → css stack, and load it for the sample.
  const [fontList, setFontList] = useState([]);
  useEffect(() => { getDescriptorFonts().then(setFontList); }, []);
  const fontKey = brand.descriptor_font || "system";
  const fontMeta = fontList.find((f) => f.key === fontKey);
  useEffect(() => { if (fontMeta?.css) loadDescriptorFont(fontMeta.css); }, [fontMeta]);
  const ds = descriptorStyles({
    css_stack: fontMeta?.stack, font_size: brand.descriptor_font_size || "medium",
    bg_opacity: brand.descriptor_bg_opacity ?? 50,
  });
  const descPos = POS[OPP[brand.logo_position] || "bottom_left"];

  return (
    <div className={`relative w-full aspect-video bg-[#050505] border border-[#262626] rounded-sm overflow-hidden ${className}`}
      data-testid="brand-preview">
      <div className="absolute inset-0 opacity-40"
        style={{ backgroundImage: "linear-gradient(#141414 1px, transparent 1px), linear-gradient(90deg, #141414 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
      <div className="absolute left-0 top-0 h-full w-1" style={{ backgroundColor: accent }} />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#333]">Program Feed</span>
      </div>
      {/* Channel Descriptor sample (shows font / size / background transparency) */}
      <div className={`absolute inset-0 flex ${descPos} p-[6%] pointer-events-none`} data-testid="brand-preview-descriptor">
        <div className="rounded-sm px-2.5 py-1.5 max-w-[55%]" style={{ ...ds.box, ...ds.text }}>
          <div className="text-white/80 font-semibold uppercase tracking-wider leading-none" style={{ fontSize: ds.labelPx }}>Now Playing</div>
          <div className="text-white font-bold leading-tight mt-0.5" style={{ fontSize: ds.titlePx }}>Sample Program Title</div>
          <div className="text-white/75 leading-snug mt-0.5" style={{ fontSize: ds.descPx }}>Channel descriptor preview</div>
        </div>
      </div>
      <div className={`absolute inset-0 flex ${pos} ${pad}`}>
        {brand.logo ? (
          <img src={brand.logo} alt="logo" className={`${size} object-contain`} style={{ opacity: (brand.opacity ?? 100) / 100 }} />
        ) : (
          <div className={`${size} aspect-square flex items-center justify-center rounded-sm border border-dashed border-[#333] text-[#444]`}
            style={{ opacity: (brand.opacity ?? 100) / 100 }}>
            <ImageSquare size={16} />
          </div>
        )}
      </div>
    </div>
  );
}
